/**
 * DynamicTraitRenderer Component
 * 
 * Dynamically renders trait components based on config.traitStyles.
 * This allows the preview to update in real-time when styles are changed.
 */

import React, { Suspense, useMemo } from 'react';
import { TraitStyleId, TraitStyleConfig, AttributeStyleConfig } from './types';

// Trait style component registries - lazy loaded
const TRAIT_COMPONENTS: Record<string, Record<string, React.LazyExoticComponent<React.ComponentType<any>>>> = {
  OnOff: {
    Style01: React.lazy(() => import('./traits/OnOff/Style01_Button').then(m => ({ default: m.Style01_Button }))),
    Style02: React.lazy(() => import('./traits/OnOff/Style02_Toggle').then(m => ({ default: m.Style02_Toggle }))),
    Style03: React.lazy(() => import('./traits/OnOff/Style03_Card').then(m => ({ default: m.Style03_Card }))),
    Style04: React.lazy(() => import('./traits/OnOff/Style04_RoundButton').then(m => ({ default: m.Style04_RoundButton }))),
    Style05: React.lazy(() => import('./traits/OnOff/Style05_TextButton').then(m => ({ default: m.Style05_TextButton }))),
  },
  Brightness: {
    Style01: React.lazy(() => import('./traits/Brightness/Style01_Slider').then(m => ({ default: m.Style01_Slider }))),
    Style02: React.lazy(() => import('./traits/Brightness/Style02_Vertical').then(m => ({ default: m.Style02_Vertical }))),
  },
  ColorSetting: {
    Style01: React.lazy(() => import('./traits/ColorSetting/Style01_Slider').then(m => ({ default: m.Style01_Slider }))),
    Style02: React.lazy(() => import('./traits/ColorSetting/Style02_SliderSimple').then(m => ({ default: m.Style02_SliderSimple }))),
  },
  HumiditySetting: {
    Style01: React.lazy(() => import('./traits/HumiditySetting/Style01_Slider').then(m => ({ default: m.Style01_Slider }))),
    Style02: React.lazy(() => import('./traits/HumiditySetting/Style02_Stepper').then(m => ({ default: m.Style02_Stepper }))),
    Style03: React.lazy(() => import('./traits/HumiditySetting/Style03_SliderMarks').then(m => ({ default: m.Style03_SliderMarks }))),
    Style04: React.lazy(() => import('./traits/HumiditySetting/Style04_Stepper').then(m => ({ default: m.Style04_Stepper }))),
  },
  FanSpeed: {
    Style01: React.lazy(() => import('./traits/FanSpeed/Style01_Buttons').then(m => ({ default: m.Style01_Buttons }))),
    Style02: React.lazy(() => import('./traits/FanSpeed/Style02_Pills').then(m => ({ default: m.Style02_Pills }))),
  },
  Mode: {
    Style01: React.lazy(() => import('./traits/Mode/Style01_Pills').then(m => ({ default: m.Style01_Pills }))),
    Style02: React.lazy(() => import('./traits/Mode/Style02_Dropdown').then(m => ({ default: m.Style02_Dropdown }))),
    Style03: React.lazy(() => import('./traits/Mode/Style03_IconCircle').then(m => ({ default: m.Style03_IconCircle }))),
    Style04: React.lazy(() => import('./traits/Mode/Style04_Pills').then(m => ({ default: m.Style04_Pills }))),
  },
  Timer: {
    Style01: React.lazy(() => import('./traits/Timer/Style01_Grid').then(m => ({ default: m.Style01_Grid }))),
    Style02: React.lazy(() => import('./traits/Timer/Style02_Grid').then(m => ({ default: m.Style02_Grid }))),
    Style03: React.lazy(() => import('./traits/Timer/Style03_Pills').then(m => ({ default: m.Style03_Pills }))),
    Style04: React.lazy(() => import('./traits/Timer/Style04_Dropdown').then(m => ({ default: m.Style04_Dropdown }))),
  },
  CustomToggles: {
    Style01: React.lazy(() => import('./traits/CustomToggles/Style01_Switches').then(m => ({ default: m.Style01_Switches }))),
    Style02: React.lazy(() => import('./traits/CustomToggles/Style02_HorizontalSwitch').then(m => ({ default: m.Style02_HorizontalSwitch }))),
    Style03: React.lazy(() => import('./traits/CustomToggles/Style03_IconPills').then(m => ({ default: m.Style03_IconPills }))),
  },
  TemperatureSetting: {
    Style01: React.lazy(() => import('./traits/TemperatureSetting/Style01_Stepper').then(m => ({ default: m.Style01_Stepper }))),
  },
  ThermostatMode: {
    Style01: React.lazy(() => import('./traits/ThermostatMode/Style01_IconButtons').then(m => ({ default: m.Style01_IconButtons }))),
  },
  OpenClose: {
    Style01: React.lazy(() => import('./traits/OpenClose/Style01_Slider').then(m => ({ default: m.Style01_Slider }))),
    Style02: React.lazy(() => import('./traits/OpenClose/Style02_Buttons').then(m => ({ default: m.Style02_Buttons }))),
  },
};

// Attribute style component registries - must match actual files in ./attributes/
const ATTRIBUTE_COMPONENTS: Record<string, Record<string, React.LazyExoticComponent<React.ComponentType<any>>>> = {
  StatusIndicator: {
    Style01: React.lazy(() => import('./attributes/StatusIndicator/Style01_Badge').then(m => ({ default: m.Style01_Badge }))),
    Style02: React.lazy(() => import('./attributes/StatusIndicator/Style02_Dot').then(m => ({ default: m.Style02_Dot }))),
    Style03: React.lazy(() => import('./attributes/StatusIndicator/Style03_Outline').then(m => ({ default: m.Style03_Outline }))),
  },
  SensorDisplay: {
    Style01: React.lazy(() => import('./attributes/SensorDisplay/Style01_Card').then(m => ({ default: m.Style01_Card }))),
    Style02: React.lazy(() => import('./attributes/SensorDisplay/Style02_Inline').then(m => ({ default: m.Style02_Inline }))),
    Style03: React.lazy(() => import('./attributes/SensorDisplay/Style03_Gauge').then(m => ({ default: m.Style03_Gauge }))),
    Style04: React.lazy(() => import('./attributes/SensorDisplay/Style04_Circle').then(m => ({ default: m.Style04_Circle }))),
    Style05: React.lazy(() => import('./attributes/SensorDisplay/Style05_Rectangle').then(m => ({ default: m.Style05_Rectangle }))),
    Style06: React.lazy(() => import('./attributes/SensorDisplay/Style06_Card').then(m => ({ default: m.Style06_Card }))),
  },
  SensorChart: {
    Style01: React.lazy(() => import('./attributes/SensorChart/Style01_Line').then(m => ({ default: m.Style01_Line }))),
    Style02: React.lazy(() => import('./attributes/SensorChart/Style02_Area').then(m => ({ default: m.Style02_Area }))),
    Style03: React.lazy(() => import('./attributes/SensorChart/Style03_DualAxis').then(m => ({ default: m.Style03_DualAxis }))),
  },
  OccupancyTimeline: {
    Style01: React.lazy(() => import('./attributes/OccupancyTimeline/Style01_Timeline').then(m => ({ default: m.Style01_Timeline }))),
  },
  AQIDisplay: {
    Style01: React.lazy(() => import('./attributes/AQIDisplay/Style01_Card').then(m => ({ default: m.Style01_Card }))),
    Style02: React.lazy(() => import('./attributes/AQIDisplay/Style02_Gauge').then(m => ({ default: m.Style02_Gauge }))),
  },
};

export interface DynamicTraitProps {
  /** Trait name (e.g., 'OnOff', 'Brightness') */
  traitName: keyof TraitStyleConfig;
  /** Style ID from config (e.g., 'Style01', 'Style02') */
  styleId?: TraitStyleId;
  /** Props to pass to the trait component */
  componentProps: any;
  /** Fallback for loading */
  loadingFallback?: React.ReactNode;
}

/**
 * DynamicTrait Component
 * 
 * Renders a trait component dynamically based on the style ID.
 */
export const DynamicTrait: React.FC<DynamicTraitProps> = ({
  traitName,
  styleId = 'Style01',
  componentProps,
  loadingFallback
}) => {
  const TraitComponent = useMemo(() => {
    const traitRegistry = TRAIT_COMPONENTS[traitName];
    if (!traitRegistry) {
      console.warn(`[DynamicTrait] Unknown trait: ${traitName}`);
      return null;
    }
    
    const component = traitRegistry[styleId] || traitRegistry['Style01'];
    if (!component) {
      console.warn(`[DynamicTrait] Unknown style ${styleId} for trait ${traitName}`);
      return null;
    }
    
    return component;
  }, [traitName, styleId]);

  if (!TraitComponent) {
    return null;
  }

  return (
    <Suspense fallback={loadingFallback || <div className="h-10 bg-slate-800/50 rounded animate-pulse" />}>
      <TraitComponent {...componentProps} />
    </Suspense>
  );
};

export interface DynamicAttributeProps {
  /** Attribute name (e.g., 'StatusIndicator', 'SensorDisplay') */
  attributeName: string;
  /** Style ID from config */
  styleId?: TraitStyleId;
  /** Props to pass to the attribute component */
  componentProps: any;
  /** Fallback for loading */
  loadingFallback?: React.ReactNode;
}

/**
 * DynamicAttribute Component
 * 
 * Renders an attribute component dynamically based on the style ID.
 */
export const DynamicAttribute: React.FC<DynamicAttributeProps> = ({
  attributeName,
  styleId = 'Style01',
  componentProps,
  loadingFallback
}) => {
  const AttributeComponent = useMemo(() => {
    const attrRegistry = ATTRIBUTE_COMPONENTS[attributeName];
    if (!attrRegistry) {
      console.warn(`[DynamicAttribute] Unknown attribute: ${attributeName}`);
      return null;
    }
    
    const component = attrRegistry[styleId] || attrRegistry['Style01'];
    if (!component) {
      console.warn(`[DynamicAttribute] Unknown style ${styleId} for attribute ${attributeName}`);
      return null;
    }
    
    return component;
  }, [attributeName, styleId]);

  if (!AttributeComponent) {
    return null;
  }

  return (
    <Suspense fallback={loadingFallback || <div className="h-10 bg-slate-800/50 rounded animate-pulse" />}>
      <AttributeComponent {...componentProps} />
    </Suspense>
  );
};

/**
 * Get the style ID for a trait from config
 */
export function getTraitStyleId(
  traitName: keyof TraitStyleConfig,
  traitStyles?: TraitStyleConfig
): TraitStyleId {
  return traitStyles?.[traitName] || 'Style01';
}

/**
 * Get the style ID for an attribute from config
 */
export function getAttributeStyleId(
  attributeName: string,
  attributeStyles?: AttributeStyleConfig | Record<string, any>
): TraitStyleId {
  if (!attributeStyles) return 'Style01';
  const style = (attributeStyles as Record<string, string>)[attributeName];
  return (style as TraitStyleId) || 'Style01';
}

export default DynamicTrait;
